import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Wrench, ListChecks, FileSpreadsheet, Upload, Coins } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { Panel, Currency, TableSkeleton, fmtDate, money, monthLabel } from './design';

// What happens after a claim is lodged: corrections to make in the Provider Portal, lines that were
// paid short or not at all, and what each payment statement took off before paying.

const KIND = {
  reduced: ['Paid short', 'badge-yellow'],
  rejected: ['Not paid', 'badge-red'],
  unmatched: ['Not matched', 'badge-gray'],
  not_on_statement: ['Not on statement', 'badge-yellow'],
  portal_error: ['Portal message', 'badge-red'],
  evidence: ['Evidence needed', 'badge-yellow'],
};
// A kind added on the server before this list knows it reads as a plain "Follow up", never as its code.
const KIND_UNKNOWN = ['Follow up', 'badge-gray'];
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date());
const qty = (n) => Number(n || 0).toLocaleString('en-AU', { maximumFractionDigits: 2 });

// What a list shows before it has rows to show: a skeleton until its first answer, or the failed
// load with a retry. An empty list here would read as "nothing to correct" or "nothing owed", which
// is a statement about money that must not be made without knowing it.
function ListState({ loading, error, onRetry }) {
  if (error) {
    return (
      <p role="alert" className="px-4 py-3 flex flex-wrap items-center gap-2 text-[13px] text-red-800">
        {error}
        {onRetry && <button type="button" onClick={onRetry} className="btn-ghost btn-sm">Try again</button>}
      </p>
    );
  }
  if (loading) return <TableSkeleton rows={2} cols={3} />;
  return null;
}

function Corrections({ rows, status, onChanged }) {
  const act = async (a, action) => {
    try { await api.post(`/aged-care/claims/amendments/${a.id}/${action}`); onChanged(); toast.success(action === 'lodged' ? 'Marked as done in the portal' : 'Back on the list'); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not update it'); }
  };
  const ready = !status.loading && !status.error;
  return (
    <Panel title="Corrections for the Provider Portal" icon={Wrench} className="mb-4" bodyClass="divide-y divide-slate-100">
      <ListState {...status} />
      {ready && !rows.length && <p className="px-4 py-3 text-[13px] text-slate-500">Nothing to correct. When a visit changes or is removed after it was claimed, the correction to make in the portal appears here.</p>}
      {ready && rows.map((a) => (
        <div key={a.id} className="px-4 py-3 text-[13px]">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-slate-900 font-medium">
              {a.participant || 'Participant'} <span className="text-slate-500 font-normal">· {fmtDate(a.service_date)} · {a.description || 'Service'}</span>
            </span>
            <span className="flex items-center gap-2">
              <span className={`badge ${a.status === 'lodged' ? 'badge-blue' : 'badge-yellow'}`}>{a.status === 'lodged' ? 'Done in the portal' : 'To do'}</span>
              {a.status === 'to_lodge'
                ? <button type="button" onClick={() => act(a, 'lodged')} className="btn-secondary btn-sm">Done in the portal</button>
                : <button type="button" onClick={() => act(a, 'reopen')} className="btn-ghost btn-sm">Undo</button>}
            </span>
          </div>
          <p className="text-slate-700 mt-0.5">
            Was {qty(a.old_units)} × {money(a.old_rate)} = {money(a.old_amount)}; now {a.kind === 'reverse' ? <b>$0 (reversed)</b> : <b>{qty(a.new_units)} × {money(a.new_rate)} = {money(a.new_amount)}</b>}.
            {a.reason ? <span className="text-slate-500"> {a.reason}</span> : null}
          </p>
          <p className="text-slate-600 mt-0.5">{a.steps}</p>
          {a.contributionNote && <p className="text-amber-800 mt-0.5">{a.contributionNote}</p>}
          {a.status === 'lodged' && <p className="text-[12px] text-slate-500 mt-0.5">Marked done {a.lodged_by_name ? `by ${a.lodged_by_name} ` : ''}on {fmtDate(a.lodged_at)}. It is settled when the next payment statement shows it.</p>}
        </div>
      ))}
    </Panel>
  );
}

// A line Services Australia paid short or did not pay: what to do with what is unpaid.
const DECISIONS = [
  ['claim_again', 'Corrected and claimed again'],
  ['write_off', 'Write it off'],
  ['charge_participant', 'Charge the participant (only if their agreement allows)'],
  ['keep', 'Leave it as it is'],
];
const NEEDS_WHY = ['write_off', 'keep'];

function FollowUps({ data, status, owners, ownersStatus, showResolved, setShowResolved, onChanged }) {
  const [resolving, setResolving] = useState(null);
  const [why, setWhy] = useState('');
  const [deciding, setDeciding] = useState(null);
  const confirm = useConfirm();
  const decide = async (f) => {
    // Writing off gives up the money for good, so it is asked first, with the amount.
    if (deciding.decision === 'write_off') {
      const short = Math.max(0, Number(f.expected ?? f.claimed ?? 0) - Number(f.paid || 0));
      const ok = await confirm(
        `${short > 0 ? `The ${money(short)} Services Australia did not pay` : 'What Services Australia did not pay'} for ${f.participant || 'this participant'}${f.service_date ? ` on ${fmtDate(f.service_date)}` : ''} is written off, and the line is resolved. Your reason is kept with the record.`,
        { title: 'Write it off?', confirmLabel: 'Write it off', danger: true },
      );
      if (!ok) return;
    }
    try {
      const r = await api.post(`/aged-care/claims/followups/${f.id}`, { action: 'decide', decision: deciding.decision, note: deciding.note });
      toast.success(r.data.invoiceNumber ? `Draft invoice ${r.data.invoiceNumber} made for the participant` : 'Recorded');
      setDeciding(null); onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not record it'); }
  };
  const act = async (f, body, done) => {
    try { await api.post(`/aged-care/claims/followups/${f.id}`, body); onChanged(); if (done) toast.success(done); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not update it'); }
  };
  const s = data.summary || {};
  const rows = data.rows || [];
  const t = today();
  const ready = !status.loading && !status.error;
  return (
    <Panel title="Lines to follow up" icon={ListChecks} className="mb-4" bodyClass="divide-y divide-slate-100"
      actions={(
        <label className="flex items-center gap-2 text-[12px] text-slate-600 cursor-pointer">
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} /> Show resolved
        </label>
      )}>
      <ListState {...status} />
      {/* Without the list of people, a line cannot be given an owner; say so rather than offer
          only "No owner". */}
      {ready && ownersStatus.error && <ListState error={ownersStatus.error} onRetry={ownersStatus.onRetry} />}
      {ready && <>
      <div className="px-4 py-2.5 flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-slate-600">
        <span><b className="text-slate-900">{s.open || 0}</b> open</span>
        <span className={s.overdue ? 'text-red-700' : ''}><b>{s.overdue || 0}</b> past their date</span>
        <span>Not yet paid: <Currency value={s.amount || 0} /></span>
        <span>Age: {s.ageing ? `${s.ageing['0-30']} under 31 days, ${s.ageing['31-60']} at 31 to 60, ${s.ageing['61+']} over 60` : '—'}</span>
        {showResolved && s.decided && (s.decided.writtenOff > 0 || s.decided.charged > 0) && (
          <span>Written off <Currency value={s.decided.writtenOff} />, charged to participants <Currency value={s.decided.charged} /></span>
        )}
      </div>
      {!rows.length && <p className="px-4 py-3 text-[13px] text-slate-500">Nothing to follow up. Lines paid short, not paid, missing from a payment statement, or flagged by the portal appear here.</p>}
      {rows.map((f) => {
        const [label, badge] = KIND[f.kind] || KIND_UNKNOWN;
        const due = f.due_date ? String(f.due_date).slice(0, 10) : '';
        return (
          <div key={f.id} className={`px-4 py-3 text-[13px] ${f.status === 'resolved' ? 'opacity-70' : ''}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-slate-900 font-medium">
                <span className={`badge ${badge} mr-2`}>{label}</span>
                {f.participant || 'Unknown participant'}
                <span className="text-slate-500 font-normal"> · {f.service_date ? fmtDate(f.service_date) : 'no date'}{f.service ? ` · ${f.service}` : ''}{f.claim_period ? ` · ${monthLabel(f.claim_period)} claim` : ''}</span>
              </span>
              <span className="text-slate-600">
                {f.claimed != null && <>Claimed <Currency value={f.claimed} /></>}
                {f.paid != null && <>, paid <Currency value={f.paid} /></>}
              </span>
            </div>
            <p className="text-slate-700 mt-0.5">{f.reason}</p>
            {f.hint && <p className="text-slate-500 mt-0.5">{f.hint}</p>}
            {f.status === 'resolved' ? (
              <p className="text-[12px] text-emerald-800 mt-1">
                Resolved {fmtDate(f.resolved_at)}: {f.resolution}
                <button type="button" onClick={() => act(f, { action: 'reopen' }, 'Reopened. It is back on the list.')} className="btn-ghost btn-sm ml-2">Reopen</button>
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <select aria-label="Owner" className="input-field !w-auto !py-1 !text-[12px]" value={f.owner_id || ''}
                  onChange={(e) => act(f, { action: 'assign', ownerId: e.target.value || null }, 'Owner set')}>
                  <option value="">No owner</option>
                  {owners.map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed user'}</option>)}
                </select>
                <input type="date" aria-label="Follow up by" className={`input-field !w-auto !py-1 !text-[12px] ${due && due < t ? '!border-red-300' : ''}`}
                  defaultValue={due} onBlur={(e) => { if (e.target.value !== due) act(f, { action: 'due', dueDate: e.target.value || null }, 'Date set'); }} />
                <span className="text-[12px] text-slate-500">{f.age_days} day{f.age_days === 1 ? '' : 's'} open</span>
                {['rejected', 'reduced'].includes(f.kind) ? (deciding && deciding.id === f.id ? (
                  <>
                    <select aria-label="What to do with what is unpaid" className="input-field !w-auto !py-1 !text-[12px]" value={deciding.decision}
                      onChange={(e) => { const v = e.target.value; setDeciding((d) => ({ ...d, decision: v })); }}>
                      {DECISIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <input aria-label="Why" className="input-field !w-56 !py-1 !text-[12px]" value={deciding.note}
                      onChange={(e) => { const v = e.target.value; setDeciding((d) => ({ ...d, note: v })); }}
                      placeholder={NEEDS_WHY.includes(deciding.decision) ? 'Why (needed for the record)' : 'Note (optional)'} />
                    <button type="button" className="btn-primary btn-sm" disabled={NEEDS_WHY.includes(deciding.decision) && !deciding.note.trim()} onClick={() => decide(f)}>Save</button>
                    <button type="button" className="btn-ghost btn-sm" onClick={() => setDeciding(null)}>Cancel</button>
                  </>
                ) : (
                  <button type="button" className="btn-secondary btn-sm" onClick={() => setDeciding({ id: f.id, decision: 'claim_again', note: '' })}>Decide</button>
                )) : resolving === f.id ? (
                  <>
                    <input aria-label="How it was resolved" autoFocus className="input-field !w-64 !py-1 !text-[12px]" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="For example: corrected and claimed again in October" />
                    <button type="button" className="btn-primary btn-sm" disabled={!why.trim()}
                      onClick={async () => { await act(f, { action: 'resolve', resolution: why }, 'Resolved'); setResolving(null); setWhy(''); }}>Save</button>
                    <button type="button" className="btn-ghost btn-sm" onClick={() => { setResolving(null); setWhy(''); }}>Cancel</button>
                  </>
                ) : (
                  <button type="button" className="btn-secondary btn-sm" onClick={() => { setResolving(f.id); setWhy(''); }}>Resolve</button>
                )}
              </div>
            )}
          </div>
        );
      })}
      </>}
    </Panel>
  );
}

const HARDSHIP = { applied: 'Hardship being assessed', approved: 'Hardship approved', declined: 'Hardship declined' };
function UnpaidContributions({ data, status }) {
  const rows = (data.rows || []).filter((r) => r.days_late > 0);
  const s = data.summary || {};
  const ready = !status.loading && !status.error;
  return (
    <Panel title="Contribution invoices unpaid" icon={Coins} className="mb-4" bodyClass="divide-y divide-slate-100">
      <ListState {...status} />
      {ready && <>
      <div className="px-4 py-2.5 flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-slate-600">
        <span>Owed: <Currency value={data.total || 0} /></span>
        {['1-7', '8-14', '15-30', '31+'].map((b) => (
          <span key={b} className={b === '31+' && s[b]?.count ? 'text-red-700' : ''}>{b} days late: <b>{s[b]?.count || 0}</b> (<Currency value={s[b]?.amount || 0} />)</span>
        ))}
      </div>
      {!rows.length && <p className="px-4 py-3 text-[13px] text-slate-500">No contribution invoice is past its due date.</p>}
      {rows.map((r) => (
        <div key={r.id} className="px-4 py-2.5 text-[13px] flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-slate-900">
            <b>{r.invoice_number}</b> · {r.participant}{r.aged_care_period ? ` · ${monthLabel(r.aged_care_period)}` : ''}
            {r.hardship_status && <span className={`badge ${r.hardship_status === 'applied' ? 'badge-blue' : 'badge-gray'} ml-2`}>{HARDSHIP[r.hardship_status] || 'Hardship'}</span>}
          </span>
          <span className={r.days_late > 30 ? 'text-red-700' : 'text-amber-800'}>
            <Currency value={r.balance} /> · {r.days_late} day{r.days_late === 1 ? '' : 's'} late{r.days_late > 30 ? ' · escalate' : r.days_late >= 7 ? ' · send a reminder' : ''}
          </span>
        </div>
      ))}
      </>}
    </Panel>
  );
}

function PaymentStatements({ rows, status }) {
  const ready = !status.loading && !status.error;
  return (
    <Panel title="Payment statements read" icon={FileSpreadsheet} className="mb-4" bodyClass="divide-y divide-slate-100">
      <ListState {...status} />
      {ready && !rows.length && <p className="px-4 py-3 text-[13px] text-slate-500">None yet. Each payment statement you upload at the reconcile step is kept here with what was taken off before payment.</p>}
      {ready && rows.map((p) => {
        const o = p.offsets || {};
        return (
          <div key={p.id} className="px-4 py-3 text-[13px]">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-slate-900 font-medium">
                {p.payment_date ? fmtDate(p.payment_date) : 'No payment date'}
                <span className="text-slate-500 font-normal">{p.payment_ref ? ` · payment reference ${p.payment_ref}` : ''}{p.naps_id ? ` · NAPS service ID ${p.naps_id}` : ''}{p.period ? ` · ${monthLabel(p.period)} claim` : ''}</span>
              </span>
              <span className="text-slate-600">{p.matched} of {p.items} lines matched</span>
            </div>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-0.5 text-slate-700">
              <span>Claim total <Currency value={o.claimTotal || 0} /></span>
              <span>Contributions <Currency value={o.contribution || 0} /></span>
              <span>Held over taken back <Currency value={o.heldoverDeducted || 0} /></span>
              <span>Paid <Currency value={o.totalPaid || 0} /></span>
              {Number(o.compensationReduction) > 0 && <span>Compensation reduction <Currency value={o.compensationReduction} /></span>}
              {Number(o.outstandingHeldover) > 0 && <span className="text-amber-800">Still held over <Currency value={o.outstandingHeldover} /></span>}
            </div>
            {Math.abs(Number(o.unexplained || 0)) > 0.005 && (
              <p className="text-amber-800 mt-0.5">{money(Math.abs(o.unexplained))} {o.unexplained < 0 ? 'less' : 'more'} was paid than the statement's own figures explain. Check the notes and categories.</p>
            )}
            {(o.otherCategories || []).length > 0 && (
              <p className="text-amber-800 mt-0.5">Not a budget: {o.otherCategories.map((c) => `${c.description || 'an amount with no description'} (${money(c.amount)})`).join(', ')}. This can be a special payment or its recovery: check the statement notes.</p>
            )}
            {(p.notes || []).length > 0 && <p className="text-slate-500 mt-0.5">Notes: {p.notes.map((n) => n.note).filter(Boolean).join(' · ')}</p>}
          </div>
        );
      })}
    </Panel>
  );
}

export default function AfterClaiming({ claims = [], refreshKey }) {
  const [amend, setAmend] = useState([]);
  const [fu, setFu] = useState({ rows: [], summary: {} });
  const [ps, setPs] = useState([]);
  const [unpaid, setUnpaid] = useState({ rows: [], summary: {}, total: 0 });
  const [owners, setOwners] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [claimId, setClaimId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Each list arrives on its own. `loaded` turns true on its first answer, so a reload keeps the
  // rows on screen instead of flashing a skeleton; `errors` holds the message from a failed load.
  const [loaded, setLoaded] = useState({});   // { amend, fu, ps, unpaid, owners }
  const [errors, setErrors] = useState({});

  const fetchInto = useCallback((key, url, apply, failMessage) => api.get(url)
    .then((r) => { apply(r.data); setLoaded((l) => ({ ...l, [key]: true })); setErrors((x) => ({ ...x, [key]: null })); })
    .catch((e) => setErrors((x) => ({ ...x, [key]: e.response?.data?.message || failMessage }))), []);
  const load = useCallback(() => {
    fetchInto('amend', '/aged-care/claims/amendments', (d) => setAmend(d || []), 'Could not load the corrections');
    fetchInto('fu', `/aged-care/claims/followups?status=${showResolved ? 'all' : 'open'}`, (d) => setFu(d || { rows: [], summary: {} }), 'Could not load the lines to follow up');
    fetchInto('ps', '/aged-care/claims/payment-statements', (d) => setPs(d || []), 'Could not load the payment statements');
    fetchInto('unpaid', '/aged-care/contributions/unpaid', (d) => setUnpaid(d || { rows: [], summary: {}, total: 0 }), 'Could not load the unpaid contribution invoices');
  }, [showResolved, fetchInto]);
  const loadOwners = useCallback(() => {
    fetchInto('owners', '/aged-care/attention/owners', (d) => setOwners(d || []), 'Could not load the people a line can be given to');
  }, [fetchInto]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { loadOwners(); }, [loadOwners]);
  const status = (key, retry = load) => ({ loading: !loaded[key] && !errors[key], error: errors[key] || null, onRetry: retry });
  useEffect(() => { if (!claimId && claims.length) setClaimId(claims[0].id); }, [claims, claimId]);

  const importDownload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !claimId) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post(`/aged-care/claims/${claimId}/portal-download`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      toast.success(`Read ${r.data.items} item${r.data.items === 1 ? '' : 's'}: ${r.data.followups.opened} to follow up`);
      load();
    } catch (e2) { toast.error(e2.response?.data?.message || 'Could not read the invoice download'); } finally { setBusy(false); }
  };

  return (
    <section className="mt-6" aria-label="After claiming">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-2">
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold text-slate-800">After claiming</h3>
          <p className="text-[12px] text-slate-500">Corrections to make in the portal, lines to chase, and what each payment statement took off.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {claims.length > 0 && (
            <select aria-label="Claim the download belongs to" className="input-field !w-auto !py-1.5 !text-[13px]" value={claimId} onChange={(e) => setClaimId(e.target.value)}>
              {claims.map((c) => <option key={c.id} value={c.id}>{monthLabel(c.period)} claim</option>)}
            </select>
          )}
          <label className={`btn-secondary btn-sm cursor-pointer ${busy || !claimId ? 'opacity-60 pointer-events-none' : ''}`}>
            <Upload size={14} /> {busy ? 'Reading' : "Import the portal's invoice download"}
            <input type="file" accept=".csv,.txt" className="hidden" disabled={busy || !claimId} onChange={importDownload} />
          </label>
        </div>
      </div>
      {result && (
        <Panel tone="muted" className="mb-4" bodyClass="px-4 py-3 text-[13px] text-slate-700 space-y-1">
          <p>{result.linked} of {result.items} item{result.items === 1 ? '' : 's'} matched to lines here{result.unknown ? `; ${result.unknown} not from Rostery` : ''}. {result.followups.opened} new to follow up, {result.followups.resolved} resolved.</p>
          {(result.messages || []).filter((m) => m.type !== 'I').map((m, i) => (
            <p key={i} className={m.type === 'E' ? 'text-red-800' : 'text-amber-800'}>
              {m.participant ? `${m.participant}: ` : ''}{m.text}{m.hint ? <span className="text-slate-600"> {m.hint}</span> : null}
            </p>
          ))}
          <button type="button" className="btn-ghost btn-sm" onClick={() => setResult(null)}>Close</button>
        </Panel>
      )}
      <Corrections rows={amend} status={status('amend')} onChanged={load} />
      <FollowUps data={fu} status={status('fu')} owners={owners} ownersStatus={status('owners', loadOwners)}
        showResolved={showResolved} setShowResolved={setShowResolved} onChanged={load} />
      <UnpaidContributions data={unpaid} status={status('unpaid')} />
      <PaymentStatements rows={ps} status={status('ps')} />
    </section>
  );
}
